import { TrialPlanDialog } from '@/features/subscribe/trial-plan-dialog'
import { useJobillaOrgId } from '@/hooks'
import { useAuth } from '@/lib'
import { ConfirmationDialog, type ConfirmationDialogRef, logger } from '@/ui'
import { Tooltip } from '@/ui/rapid-ui/tooltip'
import {
	entitlementIndexQueryKey,
	subscriptionAccessDataReason,
	subscriptionIndexQueryKey,
	useEntitlementIndex,
	userDataRole,
	useSubscriptionDestroy,
	useSubscriptionIndex,
	useUserIndex,
} from '@jobilla/nexus-sdk'
import { useQueryClient } from '@tanstack/react-query'
import { Link, useParams } from '@tanstack/react-router'
import { differenceInCalendarDays } from 'date-fns'
import { type ReactNode, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ModeBannerPill } from './mode-banner'

const ENDING_SOON_DAYS = 10

export type SubscriptionNotice =
	| { kind: 'past-due'; viaParent: boolean }
	| { kind: 'ending'; endsAt: string; viaParent: boolean }
	| { kind: 'trial'; trialEndsAt: string; isCancelled: boolean }

export function useSubscriptionNotice(): SubscriptionNotice | null {
	const organisationId = useJobillaOrgId()
	const { data: entitlements } = useEntitlementIndex(organisationId, { enabled: !!organisationId })

	const access = entitlements?.subscriptionAccess

	if (!access) {
		return null
	}

	const viaParent = access.reason === subscriptionAccessDataReason['parent-subscribed']

	if (access.pastDue) {
		return { kind: 'past-due', viaParent }
	}

	if (access.trialEndsAt) {
		return { kind: 'trial', trialEndsAt: access.trialEndsAt, isCancelled: access.endsAt !== null }
	}

	const daysLeft = access.endsAt ? differenceInCalendarDays(new Date(access.endsAt), new Date()) : null

	if (access.endsAt && daysLeft !== null && daysLeft >= 0 && daysLeft <= ENDING_SOON_DAYS) {
		return { kind: 'ending', endsAt: access.endsAt, viaParent }
	}

	return null
}

export function SubscriptionNoticeBar({ notice }: { notice: SubscriptionNotice }) {
	const { t, i18n } = useTranslation('common')
	const { can } = useAuth()

	if (notice.kind === 'past-due') {
		if (notice.viaParent) {
			return <NoticeBar message={t('subscribe.pastDue.parent')} />
		}

		if (!can('billing:manage')) {
			return <NoticeBar message={t('subscribe.pastDue.askAdmin')} />
		}

		return (
			<NoticeBar message={t('subscribe.pastDue.message')}>
				<BillingLink label={t('subscribe.pastDue.action')} />
			</NoticeBar>
		)
	}

	if (notice.kind === 'trial') {
		return <TrialNotice trialEndsAt={notice.trialEndsAt} isCancelled={notice.isCancelled} />
	}

	const date = new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium' }).format(new Date(notice.endsAt))

	if (notice.viaParent) {
		return <NoticeBar message={t('subscribe.ending.parent', { date })} />
	}

	if (!can('billing:manage')) {
		return <AskAdminNotice date={date} />
	}

	return (
		<NoticeBar message={t('subscribe.ending.message', { date })}>
			<BillingLink label={t('subscribe.ending.action')} />
		</NoticeBar>
	)
}

function NoticeBar({ message, children }: { message: string; children?: ReactNode }) {
	return (
		<div className="flex h-[var(--notice-height)] items-center justify-center gap-3 bg-amber-100 px-10 text-sm text-amber-900">
			<span>{message}</span>
			{children}
		</div>
	)
}

function BillingLink({ label }: { label: string }) {
	const { companyId } = useParams({ strict: false })

	if (!companyId) {
		return null
	}

	return (
		<Link to="/company/$companyId/settings/billing" params={{ companyId }} className="font-semibold underline">
			{label}
		</Link>
	)
}

function TrialNotice({ trialEndsAt, isCancelled }: { trialEndsAt: string; isCancelled: boolean }) {
	const { t, i18n } = useTranslation('common')
	const { can } = useAuth()
	const organisationId = useJobillaOrgId()
	const queryClient = useQueryClient()
	const confirmDialogRef = useRef<ConfirmationDialogRef>(null)
	const [isPickingPlan, setIsPickingPlan] = useState(false)
	const [hasFailed, setHasFailed] = useState(false)

	const canManage = can('billing:manage')
	const { data: subscription } = useSubscriptionIndex(organisationId, { enabled: !!organisationId && canManage })
	const profileId = subscription?.profileId ?? null

	const date = new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium' }).format(new Date(trialEndsAt))
	const daysLeft = differenceInCalendarDays(new Date(trialEndsAt), new Date())

	const cancelTrial = useSubscriptionDestroy({
		onMutate: () => setHasFailed(false),
		onSuccess: async (updated) => {
			logger.track('Subscription: trial cancelled', { category: 'billing' })
			queryClient.setQueryData(subscriptionIndexQueryKey(organisationId), updated)
			await queryClient.invalidateQueries({ queryKey: entitlementIndexQueryKey(organisationId) })
		},
		onError: (error) => {
			setHasFailed(true)
			logger.error(error, { category: 'billing', data: { action: 'cancel-trial', organisationId } })
		},
	})

	async function confirmCancel() {
		if (!profileId) { return }

		try {
			await confirmDialogRef.current?.confirm({
				title: t('subscribe.trial.cancelConfirm.title'),
				message: t('subscribe.trial.cancelConfirm.message', { date }),
				confirmText: t('subscribe.trial.cancelConfirm.confirm'),
				cancelText: t('subscribe.trial.cancelConfirm.keep'),
				destructive: true,
			})
		} catch {
			return // cancelled
		}

		cancelTrial.mutate({ organisation: organisationId, profile: profileId })
	}

	const message = hasFailed
		? t('subscribe.trial.failed')
		: isCancelled
		? t('subscribe.trial.cancelled', { date })
		: daysLeft <= 0
		? t('subscribe.trial.endsToday')
		: t('subscribe.trial.daysLeft', { count: daysLeft })

	if (!canManage || !profileId) {
		return <NoticeBar message={message} />
	}

	return (
		<NoticeBar message={message}>
			<button type="button" className="font-semibold underline" onClick={() => setIsPickingPlan(true)}>
				{isCancelled ? t('subscribe.trial.resume') : t('subscribe.trial.pickPlan')}
			</button>
			{!isCancelled && (
				<button
					type="button"
					className="font-semibold underline disabled:opacity-50"
					onClick={confirmCancel}
					disabled={cancelTrial.isPending}
				>
					{t('subscribe.trial.cancel')}
				</button>
			)}
			<TrialPlanDialog
				open={isPickingPlan}
				onClose={() => setIsPickingPlan(false)}
				organisationId={organisationId}
				profileId={profileId}
				trialEndsOn={date}
				isCancelled={isCancelled}
			/>
			<ConfirmationDialog ref={confirmDialogRef} />
		</NoticeBar>
	)
}

function AskAdminNotice({ date }: { date: string }) {
	const { t } = useTranslation('common')
	const { data: admins } = useUserIndex({ 'filter[role]': userDataRole.admin })
	const adminNames = (admins?.data ?? []).map((admin) => admin.name || admin.email)

	return (
		<NoticeBar
			message={adminNames.length > 0
				? t('subscribe.ending.askAdmins', { date, admins: adminNames.join(', ') })
				: t('subscribe.ending.askAdmin', { date })}
		/>
	)
}

// Staff are let past the paywall; this keeps them aware that the customer is not.
export function StaffAccessPill() {
	const organisationId = useJobillaOrgId()
	const { data } = useEntitlementIndex(organisationId, { enabled: !!organisationId })

	if (data?.subscriptionAccess.reason !== subscriptionAccessDataReason['jobilla-staff']) {
		return null
	}

	return (
		<Tooltip content="This customer has no subscription in force and sees the paywall. You see the app because you are Jobilla staff.">
			<span tabIndex={0}>
				<ModeBannerPill label="Customer blocked" />
			</span>
		</Tooltip>
	)
}
